import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
// we are using DraggableCore here instead of Draggable because Draggable will set
// the transform style and that will create new stacking context and will break react-mentions positioning,
// so we have to set position using left/top instead of transform: translate(...).
import { DraggableCore } from 'react-draggable';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { ResizeLine } from '@nx-frontend/shared/ui/resize-line';
import { getBorderClassNames } from '@nx-frontend/shared/themes';
import { PortalBorderType, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { AiModal } from './ai-modal';
import { ChatbotAgentMode } from './chatbot-agent-mode';
import { ChatbotAssistantMode } from './chatbot-assistant-mode';
import { ChatbotContext } from './chatbot-context';
import './chatbot-ui.css';
import { $aiThreads, $aiUIStore, resetShakeEvent, setAiHelperSizeEvent, setAIUIPositionEvent } from './store';
import { ChatbotMessage, UserLocation } from './types';

export interface ChatbotProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onClose: () => void;
  updateThreads: () => void;
  updateThreadMessages: (threadId: string) => void;
  onThreadDelete: (threadId: string) => void;
  onAddMessageToPage: (text: string, threadId: string) => void;
  onFeedback: (threadId: string, message: ChatbotMessage) => void;
  currentPortalId?: string;
  onUpdateWorkspaceIndexing: (value: boolean) => void;
  onShowUpgradePopup: () => void;
  onShowAddonPopup: () => void;
  useWeb: boolean;
  onSwitchUseWeb: (value: boolean) => void;
  /**
   * whether current ENV is development
   */
  isDev: boolean;
  currentLocation: UserLocation;
  /**
   * whether to show add to page menu item over AI assistant reply
   */
  allowCreatePage?: boolean;
  /**
   * whether to show info menu in the chatbot header
   */
  showInfoMenu?: boolean;

  noInputLineMode?: boolean;
}

export function ChatbotUi({
  onClose,
  themeColors,
  themeStyle,
  updateThreads,
  updateThreadMessages,
  onThreadDelete,
  onAddMessageToPage,
  onFeedback,
  onUpdateWorkspaceIndexing,
  onShowUpgradePopup,
  onShowAddonPopup,
  useWeb,
  onSwitchUseWeb,
  isDev,
  currentLocation,
  allowCreatePage = true,
  showInfoMenu = true,
}: ChatbotProps) {
  const [aiThreads, setAiHelperSize, setAIUIPosition, aiUI, resetShake] = useUnit([
    $aiThreads,
    setAiHelperSizeEvent,
    setAIUIPositionEvent,
    $aiUIStore,
    resetShakeEvent,
  ]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggingPosition, setDraggingPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [draggingBoundaries, setDraggingBoundaries] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const chatbotRef = useRef<HTMLDivElement>(null);
  const [documentHeight, setDocumentHeight] = useState(document.body.offsetHeight);

  const aiContext = useContext(ChatbotContext);

  const sizeStyles = useMemo(() => {
    return {
      width: `${aiThreads.size.width}px`,
      height: `${
        documentHeight < 900 ? (aiThreads.size.height > 748 ? 748 : aiThreads.size.height) : aiThreads.size.height
      }px`,
    };
  }, [aiThreads.size.height, aiThreads.size.width, documentHeight]);

  useEffect(() => {
    setDocumentHeight(document.body.offsetHeight);
    const onWindowResize = () => {
      setDocumentHeight(document.body.offsetHeight);
    };
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  useEffect(() => {
    // fix UI position in case it is rendered outside of the screen
    if (chatbotRef.current) {
      const nodeBoundingRect = chatbotRef.current.getBoundingClientRect();

      if (aiUI.x === -Infinity || aiUI.y === -Infinity) {
        // do not correct default position
        return;
      }

      let newX: number | null = null;
      let newY: number | null = null;
      if (aiUI.x < 0) {
        newX = 0;
      }
      if (aiUI.y < 0) {
        newY = 0;
      }

      // check if the window is over the bottom/right border of the screen
      if (aiUI.x + nodeBoundingRect.width > window.innerWidth) {
        newX = window.innerWidth - nodeBoundingRect.width;
      }
      if (aiUI.y + nodeBoundingRect.height > window.innerHeight) {
        newY = window.innerHeight - nodeBoundingRect.height;
      }

      if (newX !== null || newY !== null) {
        setAIUIPosition({
          x: newX !== null ? newX : aiUI.x,
          y: newY !== null ? newY : aiUI.y,
        });
      }
    }
  }, [aiUI, setAIUIPosition]);

  const onUIDragEnd = useCallback(
    (event: unknown, data: { x: number; y: number }) => {
      if (!chatbotRef.current) {
        return;
      }
      chatbotRef.current.style.transform = 'none';
      setIsDragging(false);
      setAIUIPosition(draggingPosition);
    },
    [setAIUIPosition, draggingPosition]
  );

  const onUIDragStart = useCallback(() => {
    if (!chatbotRef.current) {
      return;
    }
    const nodeBoundingRect = chatbotRef.current.getBoundingClientRect();
    setDraggingPosition({
      x: nodeBoundingRect.x,
      y: nodeBoundingRect.y,
    });

    setDraggingBoundaries({
      x: document.body.offsetWidth - nodeBoundingRect.width,
      y: document.body.offsetHeight - nodeBoundingRect.height,
    });
    setIsDragging(true);
  }, [setIsDragging]);

  const onUIDragMove = useCallback(
    (event: unknown, data: { x: number; y: number; deltaX: number; deltaY: number }) => {
      let x = Math.max(draggingPosition.x + data.deltaX, 0);
      let y = Math.max(draggingPosition.y + data.deltaY, 0);
      if (x > draggingBoundaries.x) {
        x = draggingBoundaries.x;
      }
      if (y > draggingBoundaries.y) {
        y = draggingBoundaries.y;
      }
      setDraggingPosition({
        x,
        y,
      });
    },
    [setDraggingPosition, draggingPosition, draggingBoundaries]
  );

  const handleChatbotResizeWidth = useCallback(
    (width: number) => {
      const diffWidth = width - aiThreads.size.width;
      setAiHelperSize({ width });
      if (aiUI.x >= 0) {
        setAIUIPosition({ x: aiUI.x - diffWidth, y: aiUI.y });
      }
    },
    [setAiHelperSize, aiUI, setAIUIPosition, aiThreads.size.width]
  );

  const handleChatbotResizeWidthRight = useCallback(
    (width: number) => {
      setAiHelperSize({ width });
    },
    [setAiHelperSize]
  );

  const handleChatbotResizeHeight = useCallback(
    (height: number) => {
      const diffHeight = height - aiThreads.size.height;
      setAiHelperSize({ height });
      if (aiUI.y >= 0) {
        setAIUIPosition({ x: aiUI.x, y: aiUI.y - diffHeight });
      }
    },
    [setAiHelperSize, aiUI, setAIUIPosition, aiThreads.size.height]
  );

  const handleChatbotResizeHeightBottom = useCallback(
    (height: number) => {
      setAiHelperSize({ height });
    },
    [setAiHelperSize]
  );

  const positionStyles: { left?: string; top?: string } = {};
  if (isDragging) {
    positionStyles.left = `${draggingPosition.x}px`;
    positionStyles.top = `${draggingPosition.y}px`;
  } else {
    if (aiUI.x >= 0 && aiUI.y >= 0) {
      positionStyles.left = `${aiUI.x}px`;
      positionStyles.top = `${aiUI.y}px`;
    }
  }

  useEffect(() => {
    if (aiUI.shaking) {
      setTimeout(() => {
        resetShake();
      }, 300);
    }
  }, [aiUI.shaking, resetShake]);

  return (
    <DraggableCore
      onStart={onUIDragStart}
      onStop={onUIDragEnd}
      onDrag={onUIDragMove}
      handle=".chat-bot-header"
      // position={aiUI}
      // defaultPosition={aiUI}
      nodeRef={chatbotRef}
    >
      <div
        style={{
          boxShadow: '12px 4px 80px 0px rgba(156, 163, 175, 0.24), 0px 12px 80px 0px rgba(156, 163, 175, 0.16)',
          ...sizeStyles,
          ...positionStyles,
        }}
        className={classNames(
          // z-index = 10001 because we need to be above of pdf viewer and pdf viewer has z-index 10000
          `fixed flex flex-col bottom-4 right-4 bg-white rounded-xl z-[20] max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)]`,
          { 'window-shaking': aiUI.shaking },
          getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
        )}
        ref={chatbotRef}
      >
        {aiContext.agentId ? (
          <ChatbotAgentMode smoothScroll={true} />
        ) : (
          <ChatbotAssistantMode
            showInfoMenu={showInfoMenu}
            isDev={isDev}
            onClose={onClose}
            themeColors={themeColors}
            themeStyle={themeStyle}
            onUpdateWorkspaceIndexing={onUpdateWorkspaceIndexing}
            updateThreadMessages={updateThreadMessages}
            onThreadDelete={onThreadDelete}
            onAddMessageToPage={onAddMessageToPage}
            onFeedback={onFeedback}
            allowCreatePage={allowCreatePage}
            updateThreads={updateThreads}
            onShowUpgradePopup={onShowUpgradePopup}
            onShowAddonPopup={onShowAddonPopup}
            currentLocation={currentLocation}
            useWeb={useWeb}
            onSwitchUseWeb={onSwitchUseWeb}
          />
        )}

        {/* AI Modal */}
        <AiModal themeColors={themeColors} themeStyle={themeStyle} />
        <ResizeLine
          target={chatbotRef}
          min={574}
          max={836}
          additionStyles={{ zIndex: '10' }}
          handleResize={handleChatbotResizeWidth}
          side="left"
        />
        <ResizeLine
          target={chatbotRef}
          min={574}
          max={836}
          additionStyles={{ zIndex: '10' }}
          handleResize={handleChatbotResizeWidthRight}
          side="right"
        />
        <ResizeLine
          target={chatbotRef}
          min={564}
          max={documentHeight < 900 ? 748 : 846}
          additionStyles={{ zIndex: '10' }}
          handleResize={handleChatbotResizeHeight}
          side="top"
        />
        <ResizeLine
          target={chatbotRef}
          min={564}
          max={documentHeight < 900 ? 748 : 846}
          additionStyles={{ zIndex: '10' }}
          handleResize={handleChatbotResizeHeightBottom}
          side="bottom"
        />
      </div>
    </DraggableCore>
  );
}
